import { computed, shallowRef, ref, nextTick, toValue, type MaybeRef } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { useModalOverlayStore } from '@nuxt-common/atomic-ui/app/stores/useModalOverlayStore';
import { preFetchAsyncComponents } from '~/utils/preFetchAsyncComponents';
import { defineRawAsyncComponent } from '~/utils/defineRawAsyncComponent';

import type { ModalName, BaseModalName } from '~/declaration/modals';

type ModalEntryNames = 'individual-review-modal' | 'full-review-modal' | BaseModalName;

type InitModalEntryParams = {
  name: ModalEntryNames;
  props: MaybeRef<object>;
  evtHandlers?: object;
  componentName: ModalName | null;
  aspectRatio?: number | null;
};

type ModalEntryCache = Omit<InitModalEntryParams, 'name'>;

/**
 * Composable function to open and close modal windows
 */
export const useModalStore = defineStore('pdpModalStore', () => {
  const ModalTypeComponents = {
    marxent: defineRawAsyncComponent(() => import(/* webpackChunkName: 'MarxentModal' */ '~/modules/marxent/components/MarxentModal.vue')),
    reviews: defineRawAsyncComponent(() => import(/* webpackChunkName: 'ReviewsModal' */ '~/modules/reviews/components/FullReviewModal.vue')),
    VTO: defineRawAsyncComponent(
      () => import(/* webpackChunkName: 'VTOModal' */ '@nuxt-common/virtual-try-on-experience/app/components/VirtualTryOnExperienceModal.vue'),
    ),
    'main-image-overlay': defineRawAsyncComponent(
      () => import(/* webpackChunkName: 'MainImageOverlay' */ '~/components/common/Media/ImageOverlay/MainImageOverlay.vue'),
    ),
  } as const;

  const modalOverlayStore = useModalOverlayStore();
  const { showModal } = storeToRefs(modalOverlayStore);

  const modalEntry = shallowRef<Partial<Record<ModalEntryNames, ModalEntryCache>>>({});
  const activeModal = ref<ModalEntryNames | null>(null);

  const modalProps = computed<ModalEntryCache['props']>(() => toValue(modalEntry.value?.[activeModal.value as ModalEntryNames]?.props) ?? {});
  const modalEvtHandlers = computed<ModalEntryCache['evtHandlers']>(
    () => modalEntry.value?.[activeModal.value as ModalEntryNames]?.evtHandlers ?? {},
  );

  const ModalTypeComponent = computed<(typeof ModalTypeComponents)[keyof typeof ModalTypeComponents] | null>(
    () =>
      ModalTypeComponents[
        (modalEntry.value?.[activeModal.value as ModalEntryNames]?.componentName ?? activeModal.value) as keyof typeof ModalTypeComponents
      ] ?? null,
  );
  const initModalEntry = ({ name, props = {}, evtHandlers = {}, componentName = null }: InitModalEntryParams) => {
    modalEntry.value[name] ??= {
      componentName,
      props,
      evtHandlers,
    };
  };

  /**
   * Opens named modal window
   * @param name - name of modal window to open
   */
  const open = (name: ModalEntryNames) => {
    activeModal.value = name;
    const comp = ModalTypeComponent.value;
    if (!comp) {
      activeModal.value = null;
    } else if (!comp.__asyncResolved) {
      preFetchAsyncComponents(comp)?.then(() => {
        if (showModal.value?.[name]) return;
        modalOverlayStore.setModalOverlayVisibility(name);
      });
    } else if (comp.__asyncResolved) {
      modalOverlayStore.setModalOverlayVisibility(name);
    }
  };

  /**
   * Closes named modal window
   * @param name - name of modal window to close
   */
  const close = (name: ModalEntryNames) => {
    if (showModal.value?.[name]) {
      modalOverlayStore.setModalOverlayVisibility(name);
    }
    nextTick(() => {
      activeModal.value = null;
    });
  };

  const prefetchModal = (modalName: keyof typeof ModalTypeComponents) => {
    const comp: (typeof ModalTypeComponents)[keyof typeof ModalTypeComponents] = ModalTypeComponents[modalName];
    if (comp) {
      preFetchAsyncComponents(comp);
    }
  };

  return { open, close, initModalEntry, prefetchModal, activeModal, modalProps, modalEvtHandlers, ModalTypeComponent };
});
export default useModalStore;
